import { createAsyncThunk } from "@reduxjs/toolkit";
// import { ChamaDate } from "ctech-react-components";
import Axios from "axios";
import { toast } from "utils/toast";

import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";

import logisticsApi from "services/logisticsApi";
import {
  getDealerCoverageAreaDetail,
  getDealerCoverageAreas,
} from "services/logistics/getDealerCoverageAreas";

import dealerCareApi from "services/dealerCareApi";
import {
  CoverageAreasResponse,
  WorkingHourRequest,
  WorkingHourResponse,
  UpdateWorkingHourResponse,
  UpdateWorkingHourRequest,
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest,
  DeleteWorkingAreaRequest,
  UpdateWorkingAreaStatusRequest,
  UpdateWorkingAreaStatusResponse,
  DeleteWorkingAreaResponse,
  CoverageAreasRequest,
  CoverageAreaProps,
  UpdateMapRequest,
  CreateWorkingAreaRequest,
  StartingPointsRequest,
  GetDealersRequest,
  GetDealersResponse,
  CreateWorkingAreaResponse,
  MassUpdateWorkingHoursRequest,
  MassUpdateWorkingHoursResponse,
} from "./types.store";

export const handleGetWorkingAreasRequest = createAsyncThunk<
  CoverageAreasResponse,
  CoverageAreasRequest
>("getWorkingAreas", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    const coverageAreas = await getDealerCoverageAreas(data.userId);

    return coverageAreas.sort((a: CoverageAreaProps, b: CoverageAreaProps) =>
      a.name.localeCompare(b.name),
    );
  } catch (err) {
    toast("Falha ao carregar as areas, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetWorkingAreaDetailRequest = createAsyncThunk<
  CoverageAreaProps,
  GetDealersRequest
>(
  "getWorkingAreaDetail",
  async ({ dealerId, coverageAreaId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsSpinnerVisible(true));
      return await getDealerCoverageAreaDetail(dealerId, coverageAreaId);
    } catch (err) {
      toast("Falha ao carregar a área, tente novamente!", {
        type: "error",
      });

      if (Axios.isAxiosError(err) && err && err.response) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err);
    } finally {
      dispatch(setIsSpinnerVisible(false));
    }
  },
);

export const handleUpdateWorkingAreaRequest = createAsyncThunk<
  UpdateWorkingAreaResponse,
  UpdateWorkingAreaRequest
>("putWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await logisticsApi.put(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}`,
      data,
    );

    return data;
  } catch (err) {
    toast("Falha ao atualizar área, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateWorkingAreaStatusRequest = createAsyncThunk<
  UpdateWorkingAreaStatusResponse,
  UpdateWorkingAreaStatusRequest
>("putWorkingAreaStatus", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    if (data.enabled) {
      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}/enable`,
      );
    } else {
      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}/disable`,
      );
    }

    return data;
  } catch (err) {
    toast(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      err?.response?.data?.errors?.[0]?.message ||
        "Falha ao atualizar área, tente novamente!",
      {
        type: "error",
      },
    );

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleDeleteWorkingAreaRequest = createAsyncThunk<
  DeleteWorkingAreaResponse,
  DeleteWorkingAreaRequest
>("deleteWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await logisticsApi.delete(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.id}`,
    );

    return data;
  } catch (err) {
    toast("Falha ao deletar área, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleCreateWorkingAreaRequest = createAsyncThunk<
  CreateWorkingAreaResponse,
  CreateWorkingAreaRequest
>("postWorkingArea", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const { dealerId, name, type, mapLink } = data;
    const payload: Record<string, string> = { name, type };

    if (mapLink) {
      payload.mapLink = mapLink;
    }

    const logisticsApiData = await logisticsApi.post(
      `/v1/dealers/${dealerId}/coverage-areas`,
      payload,
    );

    await dispatch(handleGetWorkingAreasRequest({ userId: dealerId }));

    return logisticsApiData.data.data;
  } catch (err) {
    toast("Falha ao criar área, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetWorkingHourRequest = createAsyncThunk<
  WorkingHourResponse,
  WorkingHourRequest
>("getWorkingHour", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    const response = await logisticsApi.get(
      `/working-area/${data.workingAreaId}/hours?api-version=1`,
    );

    return response.data;
  } catch (err) {
    toast("Falha ao carregar horários, tente novamente!", { type: "error" });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleUpdateWorkingHourRequest = createAsyncThunk<
  UpdateWorkingHourResponse,
  UpdateWorkingHourRequest
>("putWorkingHour", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));
    data.openingHours = data.openingHours.map((openingHour) => {
      return openingHour;
    });

    await logisticsApi.put(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.coverageAreaId}/opening-hours`,
      data,
    );

    return data;
  } catch (err) {
    toast("Falha ao atualizar horários, tente novamente!", {
      type: "error",
    });

    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleMassUpdateWorkingHoursRequest = createAsyncThunk<
  MassUpdateWorkingHoursResponse,
  MassUpdateWorkingHoursRequest
>("putMassWorkingHours", async (data, { rejectWithValue }) => {
  try {
    await logisticsApi.put(`/v1/dealers/${data.dealerId}/coverage-areas`, data);

    return data;
  } catch (err) {
    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  }
});

export const handleUpdateMapRequest = createAsyncThunk<null, UpdateMapRequest>(
  "putMapLink",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setIsSpinnerVisible(true));

      await logisticsApi.put(
        `/v1/dealers/${data.dealerId}/coverage-areas/${data.coverageAreaId}/map`,
        data,
      );

      await dispatch(handleGetWorkingAreasRequest({ userId: data.dealerId }));

      return null;
    } catch (err) {
      if (Axios.isAxiosError(err) && err && err.response) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err);
    } finally {
      dispatch(setIsSpinnerVisible(false));
    }
  },
);

export const handleStartingPointRequest = createAsyncThunk<
  null,
  StartingPointsRequest
>("getStartingPoint", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    await logisticsApi.put(
      `/v1/dealers/${data.dealerId}/coverage-areas/${data.coverageAreaId}/starting-point`,
      {
        coverageAreaId: data.coverageAreaId,
        dealerId: data.dealerId,
        startingPoint: {
          longitude: data.longitude,
          latitude: data.latitude,
        },
      },
    );

    return null;
  } catch (err) {
    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});

export const handleGetDealers = createAsyncThunk<
  GetDealersResponse,
  GetDealersRequest
>("getGetDealers", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(setIsSpinnerVisible(true));

    const response = await dealerCareApi.get(`/v1/dealers/${data.dealerId}`);

    if (
      response.data.data.address.latitude !== "0" &&
      response.data.data.address.latitude !== ""
    ) {
      await dispatch(
        handleStartingPointRequest({
          dealerId: data.dealerId,
          coverageAreaId: data.coverageAreaId,
          latitude: response.data.data.address.latitude,
          longitude: response.data.data.address.longitude,
        }),
      );
    }

    return response.data;
  } catch (err) {
    if (Axios.isAxiosError(err) && err && err.response) {
      return rejectWithValue(err.response.data);
    }
    return rejectWithValue(err);
  } finally {
    dispatch(setIsSpinnerVisible(false));
  }
});
